/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useEffect, useState, useMemo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import { selectProjectResults, selectSelectedVersion } from '../../project-details/data-access/project.reducer';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  deriveComplianceFromLegacyMetric,
  microeaiFairnessRiskScore,
  RISK_GREEN_MAX,
  RISK_AMBER_MAX
} from '../../Bias/fairnessEnterprise.utils';
import {
  getPlot1EligibilityGateOption,
  getPlot2EvidenceBalanceOption,
  getPlot3PolicyAndRiskOption
} from '../../Bias/fairnessEnterpriseCharts';
import FairnessChartLLM from './FairnessChartLLM';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, LineChart, CanvasRenderer]);

interface FairnessDataItem {
  Attribute: string;
  'Disparate Impact': number;
  'Equalized Odds': number;
  Risk: string;
}

interface FairnessCardProps {
  customData?: {
    ML_Fairness_Metric?: any[];
    LLM_Fairness_Metric?: any[];
    Drift?: Array<{ Event_Index?: number; Fairness_Score?: number }>;
  };
}

export const FairnessCard: React.FC<FairnessCardProps> = ({ customData }) => {
  const projectResults = useSelector(selectProjectResults); // Fetch from Redux Store
  const selectedVersion = useSelector(selectSelectedVersion); // Selected version from Redux

  const [mlFairnessData, setMlFairnessData] = useState<FairnessDataItem[]>([]);
  const [llmFairnessData, setLlmFairnessData] = useState<any[]>([]);
  const [llmChartData, setLlmChartData] = useState<any[]>([]); // Drift array for chart when available
  const [isLoading, setIsLoading] = useState<boolean>(!customData); // skip loading if custom data provided

  useEffect(() => {
    // If aggregated data from 360-analysis API is provided, use it directly
    if (customData) {
      const mlData = Array.isArray(customData.ML_Fairness_Metric) ? customData.ML_Fairness_Metric : [];
      const safeNum = (v: any) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      setMlFairnessData(
        mlData.map((row: any) => ({
          ...row,
          'Disparate Impact': safeNum(row['Disparate Impact']),
          'Equalized Odds': safeNum(row['Equalized Odds'])
        }))
      );
      const llmData = Array.isArray(customData.LLM_Fairness_Metric) ? customData.LLM_Fairness_Metric : [];
      setLlmFairnessData(llmData);
      const drift = customData?.Drift;
      if (Array.isArray(drift) && drift.length > 0) {
        const hasScores = drift.some(
          (d: any) => d != null && typeof d === 'object' && (d.Fairness_Score != null || d.fairness_score != null)
        );
        setLlmChartData(hasScores ? drift : llmData);
      } else {
        setLlmChartData(llmData);
      }
      setIsLoading(false);
      return;
    }

    // Handle array structure: fairness_metric is an array
    // Priority: top-level > collection_results > results > feature_results
    let fairnessMetricToUse = null;

    const resolveFromArray = (arr: any[] | null) => {
      if (!Array.isArray(arr) || arr.length === 0) return null;
      if (selectedVersion?.result_id) {
        const matched = arr.find((item: any) => item.result_id === selectedVersion.result_id);
        return matched || arr[0];
      }
      return arr[0];
    };

    fairnessMetricToUse =
      resolveFromArray(projectResults?.fairness_metric) ??
      resolveFromArray(projectResults?.collection_results?.fairness_metric) ??
      resolveFromArray(projectResults?.results?.fairness_metric) ??
      resolveFromArray(projectResults?.feature_results?.fairness_metric);

    if (!fairnessMetricToUse) {
      const collectionFairness = projectResults?.collection_results?.fairness_metric;
      if (Array.isArray(collectionFairness) && collectionFairness.length > 0) {
        if (selectedVersion?.result_id) {
          const matched = collectionFairness.find((item: any) => item.result_id === selectedVersion.result_id);
          fairnessMetricToUse = matched || collectionFairness[0];
        } else {
          fairnessMetricToUse = collectionFairness[0];
        }
      } else if (collectionFairness && !Array.isArray(collectionFairness)) {
        fairnessMetricToUse = collectionFairness;
      }
      // Fallback to results.fairness_metric
      else {
        const resultsFairness = projectResults?.results?.fairness_metric;
        if (Array.isArray(resultsFairness) && resultsFairness.length > 0) {
          if (selectedVersion?.result_id) {
            const matched = resultsFairness.find((item: any) => item.result_id === selectedVersion.result_id);
            fairnessMetricToUse = matched || resultsFairness[0];
          } else {
            fairnessMetricToUse = resultsFairness[0];
          }
        } else if (resultsFairness && !Array.isArray(resultsFairness)) {
          fairnessMetricToUse = resultsFairness;
        }
        // Fallback to feature_results.fairness_metric
        else {
          const featureFairness = projectResults?.feature_results?.fairness_metric;
          if (Array.isArray(featureFairness) && featureFairness.length > 0) {
            if (selectedVersion?.result_id) {
              const matched = featureFairness.find((item: any) => item.result_id === selectedVersion.result_id);
              fairnessMetricToUse = matched || featureFairness[0];
            } else {
              fairnessMetricToUse = featureFairness[0];
            }
          } else if (featureFairness && !Array.isArray(featureFairness)) {
            fairnessMetricToUse = featureFairness;
          }
        }
      }
    }

    // If still no fairness_metric, try fairness array (e.g. collection_results.fairness = [{ LLM_Fairness_Metric }])
    if (!fairnessMetricToUse) {
      const sources = [
        projectResults?.fairness,
        projectResults?.collection_results?.fairness,
        projectResults?.results?.fairness,
        projectResults?.feature_results?.fairness
      ].filter(Boolean);
      for (const arr of sources) {
        const list = Array.isArray(arr) ? arr : [arr];
        const item = selectedVersion?.result_id
          ? list.find((x: any) => x?.result_id === selectedVersion.result_id)
          : null;
        const candidate = item || list[0] || list[list.length - 1];
        if (candidate && (candidate.LLM_Fairness_Metric || candidate.ML_Fairness_Metric)) {
          fairnessMetricToUse = candidate;
          break;
        }
      }
    }

    if (fairnessMetricToUse) {
      const mlData = Array.isArray(fairnessMetricToUse?.ML_Fairness_Metric)
        ? fairnessMetricToUse.ML_Fairness_Metric
        : [];

      const safeNum = (v: any) => {
        const n = Number(v);
        return typeof n === 'number' && !Number.isNaN(n) && isFinite(n) ? n : 0;
      };
      const mlDataSafe = mlData.map((row: any) => ({
        ...row,
        'Disparate Impact': safeNum(row['Disparate Impact']),
        'Equalized Odds': safeNum(row['Equalized Odds'])
      }));

      setMlFairnessData(mlDataSafe);
      const llmData = Array.isArray(fairnessMetricToUse?.LLM_Fairness_Metric)
        ? fairnessMetricToUse.LLM_Fairness_Metric
        : [];
      setLlmFairnessData(llmData);
      // Prefer Drift for chart when it has per-event Fairness_Score
      const drift = fairnessMetricToUse?.Drift;
      if (Array.isArray(drift) && drift.length > 0) {
        const hasScores = drift.some(
          (d: any) => d != null && typeof d === 'object' && (d.Fairness_Score != null || d.fairness_score != null)
        );
        setLlmChartData(hasScores ? drift : llmData);
      } else {
        setLlmChartData(llmData);
      }
      setIsLoading(false);
    } else {
      setMlFairnessData([]);
      setLlmFairnessData([]);
      setLlmChartData([]);
      setIsLoading(false);
    }
  }, [projectResults, selectedVersion, customData]);

  // Ensure loading is false once projectResults exists
  useEffect(() => {
    if (projectResults) {
      setIsLoading(false);
    }
  }, [projectResults]);

  const { complianceRows, auditRows, riskDict } = useMemo(() => {
    const comp = deriveComplianceFromLegacyMetric(mlFairnessData);
    const audit = comp.length > 0 ? comp.map(r => ({ ...r, Mode: 'AUDIT' })) : [];
    const risk = microeaiFairnessRiskScore(comp, audit);
    return { complianceRows: comp, auditRows: audit, riskDict: risk };
  }, [mlFairnessData]);

  const plot1 = useMemo(
    () => getPlot1EligibilityGateOption(complianceRows, auditRows.length > 0 ? auditRows : null),
    [complianceRows, auditRows]
  );
  const plot2 = useMemo(() => getPlot2EvidenceBalanceOption(complianceRows), [complianceRows]);
  const plot3 = useMemo(
    () => getPlot3PolicyAndRiskOption(complianceRows, auditRows, riskDict),
    [complianceRows, auditRows, riskDict]
  );

  const chartHeight = 280;

  return (
    <>
      {isLoading && !projectResults ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <CircularProgress />
        </div>
      ) : (
        <>
          <Card className="shadow-sm border-0 mb-3">
            <Card.Body className="py-2">
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <span className="fw-semibold">{riskDict.GovernanceState}</span>
                <span className="text-muted">
                  Risk: {riskDict.Risk.toFixed(2)} ({riskDict.Band})
                </span>
                <span className="text-muted">
                  Required / Eligible: {riskDict.Required} / {riskDict.Eligible}
                </span>
                <span className="small text-muted">
                  Green ≤ {RISK_GREEN_MAX}, Amber ≤ {RISK_AMBER_MAX}
                </span>
                {llmFairnessData.length > 0 && (
                  <span className="ms-auto badge bg-success text-white">LLM: {llmFairnessData.length} records</span>
                )}
              </div>
            </Card.Body>
          </Card>
          <Row className="g-3 mt-2">
            <Col xs={12} md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="p-2">
                  {plot1 ? (
                    <ReactEChartsCore
                      echarts={echarts}
                      option={plot1}
                      notMerge
                      lazyUpdate
                      style={{ width: '100%', height: `${chartHeight}px` }}
                      opts={{ renderer: 'canvas' }}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: chartHeight }}>
                      <p className="text-muted small mb-0">No eligibility data</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="p-2">
                  {plot2 ? (
                    <ReactEChartsCore
                      echarts={echarts}
                      option={plot2}
                      notMerge
                      lazyUpdate
                      style={{ width: '100%', height: `${chartHeight}px` }}
                      opts={{ renderer: 'canvas' }}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: chartHeight }}>
                      <p className="text-muted small mb-0">No evidence balance data</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Body className="p-2">
                  {plot3 ? (
                    <ReactEChartsCore
                      echarts={echarts}
                      option={plot3}
                      notMerge
                      lazyUpdate
                      style={{ width: '100%', height: `${chartHeight}px` }}
                      opts={{ renderer: 'canvas' }}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center" style={{ height: chartHeight }}>
                      <p className="text-muted small mb-0">No risk data</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {(llmChartData.length > 0 || llmFairnessData.length > 0) && (
            <Row className="g-3 mt-4">
              <Col xs={12}>
                <FairnessChartLLM
                  data={llmChartData.length > 0 ? llmChartData : llmFairnessData}
                  title="Fairness Score by Event"
                  description="LLM Fairness analysis scores across events"
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};
